import AppContext from 'App/AppContext';
import { IPCommandButton, IPIconButton, IPSeparator, IPTooltipHost } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import WarningMessage from 'components/Notification/WarningMessage';
import {
  globalStackStylesHeight100PaddingSmall,
  globalStackTokensGapSmall,
  refreshIcon,
  sharepointIcon,
} from 'globalStyles';
import ResourceLink from 'models/resourceLink';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GraphScopeId } from 'services/Auth/authConfig';
import { getGraphSharepointErrorMsg, toAppError } from 'services/Graph/graphErrors';
import { graphGetPagesForSite, graphGetSite } from 'services/Graph/graphService';
import { ISite, ISitePage } from 'services/Graph/SharepointInterfaces';
import { navigateToExternalUrl } from 'utils/url';
import DirectoryParents from './DirectoryParents';
import MySitePages from './MySitePages';
import MySites from './MySites';

export interface ParentSiteInformation {
  tenantId?: string;
  siteId: string;
}

interface ISharepointSitePagePickerProps {
  invalidAuthToken: () => void;
  onSelect: (items: ISitePage[]) => void;
  parentSite?: ParentSiteInformation;
  listPages: ResourceLink[];
  existingLinks: ResourceLink[];
  entityContext: boolean;
  listPagesLoading: boolean;
  linkToEdit?: ResourceLink;
  singleSelect?: boolean;
}

const SharepointSitePagePicker = (props: ISharepointSitePagePickerProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['sharepoint', 'translation']);
  const [selectedSite, setSelectedSite] = useState<ISite | undefined>(undefined);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [sitePages, setSitePages] = useState<ISitePage[]>([]);
  const [sitePagesDisplay, setSitePagesDisplay] = useState<ISitePage[]>([]);
  const [spError, setSPError] = useState<string | undefined>(undefined);
  const [parentInfo, setParentInfo] = useState<ParentSiteInformation | undefined>(undefined);
  const [overrideShowOnlyExistingPages, setOverrideShowOnlyExistingPages] = useState<boolean>(false);

  //
  // Loading
  //
  const checkToken = (token: string): boolean => {
    if (!token) {
      props.invalidAuthToken();
      appContext.showNotification(t('translation:General.Notifications.TryAgainAfterConsent'), true);

      return false;
    }

    return true;
  };

  const loadParentInformation = async () => {
    if (isBusy || !props.parentSite) return;
    try {
      setIsBusy(true);
      setSPError(undefined);

      const graphInterface = await appContext.getGraphInterface(GraphScopeId.Pages, props.parentSite.tenantId);
      if (!checkToken(graphInterface.accessToken)) return;
      const _site: ISite = await graphGetSite(graphInterface.client, props.parentSite.siteId);
      if (_site) {
        _site.tenantId = props.parentSite.tenantId;
      }
      setSelectedSite(_site);
    } catch (err) {
      const graphErrorMsg = getGraphSharepointErrorMsg(err, t);
      if (graphErrorMsg) {
        setSPError(graphErrorMsg);
      } else {
        appContext.setError(toAppError(err));
      }
    } finally {
      setIsBusy(false);
    }
  };

  const fetchPages = async () => {
    if (selectedSite === undefined || !selectedSite.id) return;
    try {
      setIsBusy(true);
      setSPError(undefined);

      //get all the pages in the site
      const graphInterface = await appContext.getGraphInterface(GraphScopeId.Pages, selectedSite.tenantId);
      if (!checkToken(graphInterface.accessToken)) return;
      const _pages: ISitePage[] = await graphGetPagesForSite(graphInterface.client, selectedSite.id, false);

      _pages.forEach((l) => (l.tenantId = selectedSite.tenantId));
      setSitePages(_pages);
    } catch (err) {
      const graphErrorMsg = getGraphSharepointErrorMsg(err, t);
      if (graphErrorMsg) {
        setSPError(graphErrorMsg);
      } else {
        appContext.setError(toAppError(err));
      }
    } finally {
      setIsBusy(false);
    }
  };

  //
  // Effects
  //

  useEffect(() => {
    if (props.parentSite) {
      //changing the parentFolder prop triggers a reload: check whether this is not only a new object but has also different content
      if (props.parentSite.siteId !== parentInfo?.siteId || props.parentSite.tenantId !== parentInfo?.tenantId) {
        setParentInfo(props.parentSite);
        loadParentInformation();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.parentSite]);

  useEffect(() => {
    if (!props.listPagesLoading) {
      let override = overrideShowOnlyExistingPages;
      //when there are no listpages, set override to true because this is what the user would do immediately
      if (props.listPages.length === 0) {
        override = true;
        setOverrideShowOnlyExistingPages(true);
      }
      const newPages = filterPages(sitePages, override);
      setSitePagesDisplay(newPages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.listPagesLoading, sitePages]);

  useEffect(() => {
    fetchPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite]);

  //
  // Functions
  //
  const onOpenSharePoint = () => {
    if (!selectedSite?.webUrl) return;
    navigateToExternalUrl(selectedSite.webUrl + '/SitePages', undefined, '');
  };

  const updateSiteSelection = (site?: ISite) => {
    setSelectedSite(site);
    setSitePages([]);
  };

  const showAllPagesNotInLibrary = () => {
    const newPages = filterPages(sitePages, true);
    setSitePagesDisplay(newPages);
    setOverrideShowOnlyExistingPages(true);
  };

  const filterPages = (pages: ISitePage[], overrideShowOnlyPagesInLibrary: boolean): ISitePage[] => {
    let newPages: ISitePage[] = [...pages];
    if (props.entityContext && !overrideShowOnlyPagesInLibrary) {
      //show only pages that are already in the library and not in the list with existing items
      newPages = pages.filter((p) => props.listPages.some((l) => l.pageId === p.id));
    }

    return newPages;
  };

  const onRefresh = () => {
    fetchPages();
    setOverrideShowOnlyExistingPages(false);
  };

  const selectSitePage = (items?: ISitePage[]) => {
    if (items) props.onSelect(items);
  };

  //
  // Render
  //
  return (
    <IPStack verticalFill>
      {/* Body Left and Right*/}
      <IPStack.Item grow>
        <IPStack horizontal styles={globalStackStylesHeight100PaddingSmall} tokens={globalStackTokensGapSmall}>
          {/* Left Groups Part*/}
          {!props.parentSite && (
            <IPStack.Item styles={{ root: { width: '30%', padding: 5 } }}>
              <MySites
                onSelect={(item) => updateSiteSelection(item)}
                selectedSite={selectedSite}
                checkToken={checkToken}
                authType={GraphScopeId.Pages}
              />
              <IPSeparator vertical />
            </IPStack.Item>
          )}
          {/* Right Part*/}
          <IPStack.Item grow styles={{ root: { width: !props.parentSite ? '70%' : '100%', padding: 5 } }}>
            <IPStack verticalFill>
              {(props.parentSite || selectedSite) && (
                <IPStack.Item>
                  <IPStack horizontal wrap horizontalAlign="space-between">
                    <IPStack.Item>
                      <DirectoryParents
                        parents={[]}
                        site={selectedSite}
                        onSelectDrive={() => {}}
                        onSelectDriveFolder={() => {}}
                        onBack={() => {}}
                        onSelectSite={() => {}}
                      />
                    </IPStack.Item>
                    <IPStack.Item>
                      {!overrideShowOnlyExistingPages && props.entityContext && (
                        <IPTooltipHost content={t('sharepoint:SitePagePicker.ShowAllFilter')}>
                          <IPCommandButton
                            iconProps={{ iconName: 'ClearFilter' }}
                            onClick={() => showAllPagesNotInLibrary()}
                          ></IPCommandButton>
                        </IPTooltipHost>
                      )}
                      {selectedSite && (
                        <IPTooltipHost content={t('sharepoint:CommandBar.OpenSharePoint')}>
                          <IPIconButton iconProps={sharepointIcon} onClick={onOpenSharePoint} />
                        </IPTooltipHost>
                      )}
                      <IPTooltipHost content={t('sharepoint:CommandBar.Refresh')}>
                        <IPIconButton iconProps={refreshIcon} onClick={onRefresh} />
                      </IPTooltipHost>
                    </IPStack.Item>
                  </IPStack>
                </IPStack.Item>
              )}
              {spError && !isBusy && (
                <IPStack verticalFill>
                  <WarningMessage message={spError} onDismiss={undefined}></WarningMessage>
                </IPStack>
              )}
              {!spError && selectedSite && (
                <IPStack.Item grow>
                  <MySitePages
                    pages={sitePagesDisplay}
                    onSelect={(items: ISitePage[]) => {
                      selectSitePage(items);
                    }}
                    isListLoading={isBusy}
                    existingLinks={props.existingLinks}
                    linkToEdit={props.linkToEdit}
                    singleSelect={props.singleSelect}
                  />
                </IPStack.Item>
              )}
            </IPStack>
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );
};

export default SharepointSitePagePicker;
